import { useLocalization } from "@fluent/react"
import { alpha } from "@mui/material/styles"
import { Box, Button, Collapse, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled"
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useNavigate } from "react-router-dom"
import { useAppSelector } from "../../../app/hooks"
import { selectSettings } from "../../settings/settingsSlice"
import type { OperationalDashboardOverviewDto } from "../operationalDashboards"
import { MeetingElapsedTime } from "../minuteMettingNextVersion/MeetingElapsedTime"
import { OpenMeetingMinutesButton } from "./OpenMeetingMinutesButton"
import { usePackage } from "../../permissions/usePackage"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"

type MeetingPanelData = Pick<OperationalDashboardOverviewDto,
    "meetingMinutesId" | "meetingMinutesStatus" | "meetingName" | "lastMeetingStartDate" | "lastMeetingEndDate" | "plannedMeetingTime">

type OperationalDashboardMeetingPanelProps = {
    data: MeetingPanelData
    dashboardId: number
    meetingStatus?: string
    showHeartbeat: boolean
    detailsOpen: boolean
    onToggleDetails: () => void
    onEndMeeting: () => void
}

export const OperationalDashboardMeetingPanel = ({
    data, dashboardId, meetingStatus, showHeartbeat, detailsOpen, onToggleDetails, onEndMeeting,
}: OperationalDashboardMeetingPanelProps) => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const navigate = useNavigate()
    const canWrite = usePackage("MeettingMinutes", "ReadWrite")
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)
    const isInMeeting = meetingStatus === "INMEETING"
    const statusLabel = meetingStatus === "INMEETING" ? label("meeting-in-progress", "_W trakcie spotkania")
        : meetingStatus === "SUSPENDED" ? label("meeting-suspended", "Spotkanie zawieszone")
            : meetingStatus === "CLOSED" ? label("meeting-closed", "Spotkanie zakończone")
                : meetingStatus === "NEW" ? label("meeting-new", "Spotkanie nierozpoczęte")
                    : meetingStatus ? `${label("meeting-status-unknown", "Nieznany status spotkania")}: ${data.meetingMinutesStatus}`
                        : label("meeting-status-unavailable", "Status spotkania niedostępny")
    const dateLocale = locale === "pl" ? "pl-PL" : locale === "de" ? "de-DE" : "en-US"
    const formatMeetingDate = (value: string) => {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? label("meeting-date-invalid", "Nieprawidłowa data")
            : new Intl.DateTimeFormat(dateLocale, {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            }).format(date)
    }
    const detailsLabel = label(detailsOpen ? "meeting-details-hide" : "meeting-details-show",
        detailsOpen ? "Zwiń szczegóły spotkania" : "Pokaż szczegóły spotkania")

    return (
        <Stack sx={{ width: { xs: "100%", lg: "auto" }, maxWidth: { lg: 760 }, minWidth: 0, flexShrink: 1 }}>
            <Paper variant="outlined" sx={{
                p: 1, borderRadius: 2, backgroundImage: "none", minWidth: 0,
                bgcolor: "background.paper", borderColor: "divider",
            }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" gap={1.5}>
                    <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" gap={0.75} sx={{ minWidth: 0 }}>
                        <Box sx={theme => {
                            const palette = isInMeeting ? theme.palette.primary
                                : meetingStatus === "SUSPENDED" ? theme.palette.warning : null
                            return {
                                display: "inline-flex", alignItems: "center", gap: 0.75,
                                px: 1, py: 0.625, borderRadius: 1.5, minWidth: 0,
                                bgcolor: isInMeeting ? theme.palette.primary.main
                                    : palette ? alpha(palette.main, 0.14) : theme.palette.action.hover,
                                color: isInMeeting ? theme.palette.primary.contrastText
                                    : palette ? theme.palette.mode === "dark" ? palette.light : palette.dark
                                        : theme.palette.text.secondary,
                            }
                        }}>
                            <Box aria-hidden="true" sx={{
                                width: 7, height: 7, flexShrink: 0, borderRadius: "50%", bgcolor: "currentColor",
                            }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                                {statusLabel}
                            </Typography>
                        </Box>
                        {isInMeeting && data.lastMeetingStartDate && (
                            <Box sx={{ flexShrink: 0 }}>
                                <MeetingElapsedTime
                                    startDate={data.lastMeetingStartDate}
                                    plannedMinutes={data.plannedMeetingTime}
                                    showPulse={showHeartbeat}
                                    showRemainingMinutes={showHeartbeat} />
                            </Box>
                        )}
                        <Tooltip title={detailsLabel}>
                            <IconButton size="small" aria-label={detailsLabel}
                                aria-expanded={detailsOpen} aria-controls={`meeting-details-${dashboardId}`}
                                onClick={onToggleDetails}
                                sx={{
                                    color: "text.secondary",
                                    transform: detailsOpen ? "rotate(180deg)" : "none",
                                    transition: "transform 200ms",
                                }}>
                                <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" useFlexGap flexWrap="wrap" gap={1}
                        sx={{ ml: "auto", minWidth: 0 }}>
                        <Box sx={theme => ({
                            display: "flex", alignItems: "center", lineHeight: 0,
                            "& .MuiButton-root, & .MuiIconButton-root": {
                                height: 36, borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                border: "1px solid",
                                borderColor: alpha(theme.palette.primary.main, 0.25),
                                color: theme.palette.primary.main,
                                textTransform: "none", fontWeight: 600, whiteSpace: "nowrap",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                            },
                        })}>
                            <OpenMeetingMinutesButton meetingMinutesId={data.meetingMinutesId} compact />
                        </Box>
                        {canWrite && (meetingStatus === "NEW" || meetingStatus === "CLOSED") && (
                            <Tooltip title={data.meetingMinutesId?.trim() ? ""
                                : label("meeting-minutes-unavailable", "Brak protokołu spotkania potrzebnego do rozpoczęcia.")}>
                                <span>
                                    <Button size="small" variant="contained" startIcon={<PhoneInTalkIcon />}
                                        disabled={!data.meetingMinutesId?.trim()}
                                        onClick={() => {
                                            if (data.meetingMinutesId?.trim()) {
                                                navigate(`/operational-dashboards/meeting/create/${encodeURIComponent(data.meetingMinutesId)}?dashboardId=${dashboardId}`)
                                            }
                                        }}
                                        sx={{ height: 36, whiteSpace: "nowrap" }}>
                                        {l10n.getString("meeting-minutes-start-meeting", null, "_Rozpocznij spotkanie")}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {canWrite && isInMeeting && data.meetingMinutesId?.trim() && (
                            <Button size="small" variant="outlined" color="error" startIcon={<PhoneDisabledIcon />}
                                onClick={onEndMeeting} sx={{ height: 36, whiteSpace: "nowrap" }}>
                                {label("meeting-end", "Zakończ spotkanie")}
                            </Button>
                        )}
                    </Stack>
                </Stack>
                <Collapse in={detailsOpen} id={`meeting-details-${dashboardId}`}>
                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: "divider" }}>
                        <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.75 }}>
                            <ScheduleOutlinedIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>{label("meeting", "Spotkanie")}</Typography>
                        </Stack>
                        {data.meetingName && (
                            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                                {data.meetingName}
                            </Typography>
                        )}
                        {data.lastMeetingStartDate || data.lastMeetingEndDate ? (
                            <Stack spacing={0.25} sx={{ mt: data.meetingName ? 0.75 : 0 }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                    {label(isInMeeting ? "meeting-current-details" : "meeting-previous-details",
                                        isInMeeting ? "Bieżące spotkanie" : "Ostatnie spotkanie")}
                                </Typography>
                                {data.lastMeetingStartDate && (
                                    <Typography variant="caption" color="text.secondary">
                                        {label("meeting-started", "Rozpoczęto")}: {formatMeetingDate(data.lastMeetingStartDate)}
                                    </Typography>
                                )}
                                {data.lastMeetingEndDate && (
                                    <Typography variant="caption" color="text.secondary">
                                        {label("meeting-ended", "Zakończono")}: {formatMeetingDate(data.lastMeetingEndDate)}
                                    </Typography>
                                )}
                            </Stack>
                        ) : !data.meetingName && (
                            <Typography variant="body2" color="text.secondary">
                                {label("no-meeting-data", "Brak danych spotkania.")}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        </Stack>
    )
}
